'use client'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

interface AnimatedProgressBarProps {
  value: number
  className?: string
  indicatorClassName?: string
  animateOnMount?: boolean
  /** Accessible name (aria-label). */
  label?: string
  /** Human-readable value for assistive tech (aria-valuetext), e.g. counts alongside percentage. */
  valueText?: string
}

export function AnimatedProgressBar({
  value,
  className,
  indicatorClassName,
  animateOnMount = true,
  label,
  valueText,
}: AnimatedProgressBarProps): React.JSX.Element {
  const target = Math.max(0, Math.min(value, 100)) / 100
  const [displayed, setDisplayed] = useState<number>(animateOnMount ? 0 : target)

  useEffect(() => {
    if (animateOnMount) {
      // Defer to next frame so the CSS transition runs from 0 → target.
      const raf = requestAnimationFrame(() => setDisplayed(target))
      return () => cancelAnimationFrame(raf)
    }
    setDisplayed(target)
  }, [target, animateOnMount])

  return (
    <div
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
      {...(valueText != null && valueText !== '' ? { 'aria-valuetext': valueText } : {})}
      aria-label={label ?? `Progresso: ${value}%`}
      className={cn('relative h-1.5 w-full overflow-hidden rounded-full bg-secondary', className)}
    >
      <div
        style={{
          transform: `scaleX(${displayed})`,
          transformOrigin: '0 0',
          transitionProperty: 'transform',
          transitionDuration: '1000ms',
          transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
        }}
        className={cn('absolute inset-y-0 left-0 w-full rounded-full', indicatorClassName)}
      />
    </div>
  )
}
